import type { FieldConstraint } from "@tfg/db"
import { t } from "i18next"
import { z } from "zod"

import { CustomFieldConstraint } from "@tfg/types/endpoints/traceability"

type CustomFieldDefinition = {
  constraint: FieldConstraint
  min: number | null
  max: number | null
}

export function hasMoreThanOneValue(
  array: string[]
): array is [string, ...string[]] {
  return array.length > 1
}

const selectParser = (enumValues: string[]) => {
  const values: [string, ...string[]] = hasMoreThanOneValue(enumValues)
    ? enumValues
    : [enumValues[0]]

  const parser = z.union([
    z.literal(""),
    z.enum(values, {
      errorMap: () => ({ message: t("traceability:requiredError") }),
    }),
  ])
  return parser
}

const standardParser = (enumValues: string[]) => {
  return selectParser(enumValues).optional()
}

const multiSelectParser = (enumValues: string[]) => {
  const values: [string, ...string[]] = hasMoreThanOneValue(enumValues)
    ? enumValues
    : [enumValues[0]]

  return z
    .string()
    .nonempty({
      message: t("traceability:requiredError"),
    })
    .transform((value) => JSON.parse(value))
    .pipe(
      z.array(
        z.enum(values, {
          errorMap: () => ({ message: t("traceability:requiredError") }),
        })
      )
    )
    .transform((value) => JSON.stringify(value))
}

const numberParser = (
  _enumValues: string[],
  customFieldDefinition: CustomFieldDefinition
) => {
  let parser = z.coerce.number({
    invalid_type_error: t("traceability:numberError"),
  })

  if (customFieldDefinition.min !== null) {
    parser = parser.min(customFieldDefinition.min, {
      message: t("traceability:minError", { min: customFieldDefinition.min }),
    })
  }
  if (customFieldDefinition.max !== null) {
    parser = parser.max(customFieldDefinition.max, {
      message: t("traceability:maxError", { max: customFieldDefinition.max }),
    })
  }

  return z
    .string()
    .min(1, { message: t("traceability:requiredError") })
    .pipe(parser.transform(transformDigits))
}

const customParsers = {
  [CustomFieldConstraint.SELECT]: selectParser,
  [CustomFieldConstraint.STANDARD]: standardParser,
  [CustomFieldConstraint.MULTI_SELECT]: multiSelectParser,
  [CustomFieldConstraint.NUMBER]: numberParser,
  [CustomFieldConstraint.STRING]: () => z.string(),
}

export const getCustomFieldParser = (
  cfd: CustomFieldDefinition,
  enumValues: string[]
) => {
  return customParsers[cfd.constraint](enumValues, cfd)
}

function transformDigits(value: number) {
  const stringValue = value.toString()
  if (stringValue.length <= 1) return stringValue
  if (stringValue.includes(".")) {
    return value.toFixed(2).toString()
  }
  return stringValue
}
